// ... existing code ...
import { useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import ProductGrid from "./components/ProductGrid";
import { useProducts } from "../../hooks/useProducts";

export default function SubscribeTrial() {
  const navigate = useNavigate();
  const { products, loading, error } = useProducts("trial");

  const handleOrder = (productId: string) => {
    navigate(`/product/${productId}`);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main className="pt-20 md:pt-24">
        {/* Hero */}
        <section className="bg-stone-900 py-14 md:py-20">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="max-w-3xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-rose-700/30 text-rose-300 mb-5">
                <i className="ri-sparkling-line" />
                <span className="font-body text-xs font-semibold">新手專屬認購區</span>
              </div>
              <h1 className="font-display text-3xl md:text-5xl font-bold text-white mb-4">
                認購體驗區
              </h1>
              <p className="font-body text-sm md:text-base text-stone-400 leading-relaxed max-w-xl mx-auto">
                僅限一般註冊會員下單。每件商品達標即出貨，低認購價、穩定利潤，是入門供應鏈認購的最佳起點。
              </p>
            </div>
          </div>
        </section>

        {/* Product Grid Section */}
        <section className="py-12 md:py-16 bg-stone-50">
          <div className="w-full px-4 md:px-8 lg:px-12">
            {/* Section header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8 md:mb-10">
              <div>
                <h2 className="font-display text-xl md:text-2xl font-bold text-stone-800 mb-1">
                  新手專屬體驗商品
                </h2>
                <p className="font-body text-xs md:text-sm text-stone-500">
                  共 {products.length} 件貨源開放認購中，認購價與銷售價一目了然
                </p>
              </div>
              <Link
                to="/vip-subscribe"
                className="inline-flex items-center gap-2 font-body text-xs md:text-sm font-semibold px-4 py-2.5 rounded-full border border-stone-300 text-stone-700 hover:border-amber-400 hover:text-amber-600 transition-colors whitespace-nowrap self-start sm:self-auto"
              >
                <i className="ri-vip-crown-line" />
                升級 VIP 解鎖更高利潤
              </Link>
            </div>

            {error && (
              <div className="mb-4 font-body text-sm px-4 py-3 rounded-lg bg-red-50 text-red-600 border border-red-200">
                {error}
              </div>
            )}

            {loading && products.length === 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div
                    key={i}
                    className="bg-white rounded-2xl border border-stone-200 overflow-hidden"
                  >
                    <div className="w-full h-48 md:h-56 bg-stone-100 animate-pulse" />
                    <div className="p-4 md:p-5 space-y-3">
                      <div className="h-5 bg-stone-100 rounded animate-pulse w-3/4" />
                      <div className="h-4 bg-stone-100 rounded animate-pulse w-full" />
                      <div className="h-4 bg-stone-100 rounded animate-pulse w-2/3" />
                      <div className="h-8 bg-stone-100 rounded animate-pulse w-full mt-2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <ProductGrid products={products} onOrder={handleOrder} />
            )}
          </div>
        </section>

        {/* Info Banner */}
        <section className="py-10 md:py-14 bg-white border-t border-stone-100">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-8">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-rose-50 flex items-center justify-center shrink-0">
                  <i className="ri-shield-check-line text-rose-600 text-lg" />
                </div>
                <div>
                  <h3 className="font-display text-sm font-semibold text-stone-800 mb-1">
                    達標即出貨
                  </h3>
                  <p className="font-body text-xs text-stone-500 leading-relaxed">
                    認購達標後立即安排出貨，未達標則全額退款。
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
                  <i className="ri-coin-line text-emerald-600 text-lg" />
                </div>
                <div>
                  <h3 className="font-display text-sm font-semibold text-stone-800 mb-1">
                    穩定利潤空間
                  </h3>
                  <p className="font-body text-xs text-stone-500 leading-relaxed">
                    體驗區商品利潤空間透明可見，銷售價與認購價一目了然。
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center shrink-0">
                  <i className="ri-refresh-line text-amber-600 text-lg" />
                </div>
                <div>
                  <h3 className="font-display text-sm font-semibold text-stone-800 mb-1">
                    隨時可升級
                  </h3>
                  <p className="font-body text-xs text-stone-500 leading-relaxed">
                    體驗滿意後可隨時升級 VIP，解鎖更高利潤與專屬貨源。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}